import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { Send as SendIcon } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import { logError } from '../../shared/services/logger.service';
import { SavaquanHttpError } from '../../shared/utils/http-error';
import {
  CommunicationRecipientDto,
  DeliveryChannel,
  SendCommunicationResponseDto,
  DocumentType,
  SendCommunicationParams,
} from './dtos/communication.dto';
import { sendCommunication, getMessageContent } from './services/communication.service';
import { RecipientsCard, DeliveryMethodCard } from './cards';

const generateId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const formatPhoneNumber = (value: string): string => {
  const cleaned = ('' + value).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
  if (match) {
    const part1 = match[1] ? `(${match[1]}` : '';
    const part2 = match[2] ? `) ${match[2]}` : '';
    const part3 = match[3] ? `-${match[3]}` : '';
    return `${part1}${part2}${part3}`;
  }
  return value;
};

const isValidEmail = (email: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const isValidPhone = (phone: string): boolean => {
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.length >= 10;
};

export interface CommunicationEditorProps {
  documentType: DocumentType;
  documentId: number;
  primaryContact?: {
    name?: string;
    email?: string;
    phoneNumber?: string;
  };
  additionalContacts?: CommunicationRecipientDto[];
  onSendComplete?: (response: SendCommunicationResponseDto) => void;
  clientName?: string;
  sendDisabled?: boolean;
  sendDisabledReason?: string;
  isFollowUp?: boolean;
  sx?: SxProps<Theme>;
}

export const CommunicationEditor: React.FC<CommunicationEditorProps> = ({
  documentType,
  documentId,
  primaryContact,
  additionalContacts = [],
  onSendComplete,
  clientName,
  sendDisabled = false,
  sendDisabledReason = '',
  isFollowUp = false,
  sx,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });

  const [recipients, setRecipients] = useState<CommunicationRecipientDto[]>([]);
  const [selectedRecipientIds, setSelectedRecipientIds] = useState<Set<string>>(new Set());

  const [showAddRecipient, setShowAddRecipient] = useState(false);
  const [newRecipientName, setNewRecipientName] = useState('');
  const [newRecipientEmail, setNewRecipientEmail] = useState('');
  const [newRecipientPhone, setNewRecipientPhone] = useState('');

  // Both email and SMS selected by default
  const [deliveryChannels, setDeliveryChannels] = useState<Set<DeliveryChannel>>(
    new Set(['email', 'sms'] as DeliveryChannel[])
  );

  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    const initialRecipients: CommunicationRecipientDto[] = [];
    const initialSelected = new Set<string>();

    if (primaryContact && (primaryContact.email || primaryContact.phoneNumber)) {
      const primaryId = 'primary';
      initialRecipients.push({
        id: primaryId,
        name: primaryContact.name || clientName,
        email: primaryContact.email,
        phoneNumber: primaryContact.phoneNumber,
        isPrimary: true,
      });
      initialSelected.add(primaryId);
    }

    additionalContacts.forEach((contact, index) => {
      const contactId = contact.id || `additional_${index}`;
      initialRecipients.push({
        ...contact,
        id: contactId,
      });
    });

    setRecipients(initialRecipients);
    setSelectedRecipientIds(initialSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChannelToggle = useCallback((channel: DeliveryChannel) => {
    setDeliveryChannels(prev => {
      const newChannels = new Set(prev);
      if (newChannels.has(channel)) {
        // Allow unchecking even if it's the last one
        newChannels.delete(channel);
      } else {
        newChannels.add(channel);
      }
      return newChannels;
    });
  }, []);

  const handleRecipientToggle = useCallback((recipientId: string) => {
    setSelectedRecipientIds(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(recipientId)) {
        newSelected.delete(recipientId);
      } else {
        newSelected.add(recipientId);
      }
      return newSelected;
    });
  }, []);

  const handleCancelAddRecipient = () => {
    setShowAddRecipient(false);
    setNewRecipientName('');
    setNewRecipientEmail('');
    setNewRecipientPhone('');
  };

  const handleAddRecipient = () => {
    const email = newRecipientEmail.trim();
    const phone = newRecipientPhone.trim();
    const name = newRecipientName.trim();

    if (!email && !phone) {
      enqueueSnackbar('Please enter an email or phone number', { variant: 'warning' });
      return;
    }

    if (email && !isValidEmail(email)) {
      enqueueSnackbar('Please enter a valid email address', { variant: 'warning' });
      return;
    }

    if (phone && !isValidPhone(phone)) {
      enqueueSnackbar('Please enter a valid phone number (10 digits)', { variant: 'warning' });
      return;
    }

    const newId = generateId();
    const newRecipient: CommunicationRecipientDto = {
      id: newId,
      name: name || undefined,
      email: email || undefined,
      phoneNumber: phone || undefined,
      isNew: true,
    };

    setRecipients(prev => [...prev, newRecipient]);
    setSelectedRecipientIds(prev => new Set([...Array.from(prev), newId]));

    setShowAddRecipient(false);
    setNewRecipientName('');
    setNewRecipientEmail('');
    setNewRecipientPhone('');
  };

  const handleRemoveRecipient = useCallback((recipientId: string) => {
    setRecipients(prev => prev.filter(r => r.id !== recipientId));
    setSelectedRecipientIds(prev => {
      const newSelected = new Set(prev);
      newSelected.delete(recipientId);
      return newSelected;
    });
  }, []);

  const getValidRecipientsForSend = useCallback(() => {
    const selected = recipients.filter(r => selectedRecipientIds.has(r.id));
    return selected.filter(r => {
      const hasValidEmail = deliveryChannels.has('email') && r.email && isValidEmail(r.email);
      const hasValidPhone = deliveryChannels.has('sms') && r.phoneNumber && isValidPhone(r.phoneNumber);
      return hasValidEmail || hasValidPhone;
    });
  }, [recipients, selectedRecipientIds, deliveryChannels]);

  const handleSend = useCallback(async () => {
    const validRecipients = getValidRecipientsForSend();

    if (validRecipients.length === 0) {
      enqueueSnackbar('Please select at least one recipient with valid contact information', { variant: 'warning' });
      return;
    }

    if (deliveryChannels.size === 0) {
      enqueueSnackbar('Please select at least one delivery method', { variant: 'warning' });
      return;
    }

    setIsSending(true);

    try {
      // Fetch the filled message content from the backend
      const messageContent = await getMessageContent(navigate, documentType, documentId, isFollowUp);

      const communicationParams: SendCommunicationParams = {
        documentType,
        documentId,
        recipients: validRecipients,
        deliveryChannels: Array.from(deliveryChannels),
        subject: messageContent.subject,
        emailBody: messageContent.emailBody,
        smsBody: messageContent.smsBody,
        isFollowUp,
      };

      const response = await sendCommunication(navigate, communicationParams);

      const sentSummary: string[] = [];
      if (response.emailsSent && response.emailsSent > 0) {
        sentSummary.push(`${response.emailsSent} email(s)`);
      }
      if (response.smsSent && response.smsSent > 0) {
        sentSummary.push(`${response.smsSent} SMS`);
      }

      if (sentSummary.length > 0) {
        enqueueSnackbar(`Successfully sent ${sentSummary.join(' and ')}!`, { variant: 'success' });
      } else {
        enqueueSnackbar('Communication sent successfully!', { variant: 'success' });
      }
      onSendComplete?.(response);
    } catch (error) {
      logError('Error sending communication', { documentType, documentId }, error as Error | undefined);
      
      if (error instanceof SavaquanHttpError && error.statusCode === 400) {
        enqueueSnackbar(error.message, { variant: 'error' });
      } else {
        enqueueSnackbar('Failed to send communication. Please try again.', { variant: 'error' });
      }
    } finally {
      setIsSending(false);
    }
  }, [
    getValidRecipientsForSend,
    documentType,
    documentId,
    deliveryChannels,
    isFollowUp,
    navigate,
    onSendComplete,
  ]);

  const validRecipientsCount = getValidRecipientsForSend().length;
  const canSend = validRecipientsCount > 0 && deliveryChannels.size > 0;

  return (
    <Box sx={sx}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <RecipientsCard
            recipients={recipients}
            selectedRecipientIds={selectedRecipientIds}
            onRecipientToggle={handleRecipientToggle}
            onRemoveRecipient={handleRemoveRecipient}
            showAddRecipient={showAddRecipient}
            onShowAddRecipient={setShowAddRecipient}
            newRecipientName={newRecipientName}
            newRecipientEmail={newRecipientEmail}
            newRecipientPhone={newRecipientPhone}
            onNewRecipientNameChange={setNewRecipientName}
            onNewRecipientEmailChange={setNewRecipientEmail}
            onNewRecipientPhoneChange={value => setNewRecipientPhone(formatPhoneNumber(value))}
            onAddRecipient={handleAddRecipient}
            onCancelAddRecipient={handleCancelAddRecipient}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DeliveryMethodCard
            deliveryChannels={deliveryChannels}
            onChannelToggle={handleChannelToggle}
          />
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Tooltip
              title={sendDisabledReason || (!canSend ? 'Select at least one recipient and delivery method' : '')}
            >
              <span style={{ width: isMobile ? '100%' : 'auto' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSend}
                  disabled={sendDisabled || isSending || !canSend}
                  startIcon={isSending ? <CircularProgress size={16} /> : <SendIcon />}
                  fullWidth={isMobile}
                  size="large"
                >
                  {isSending ? 'Sending...' : 'Send'}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CommunicationEditor;
